import { useMemo, useState } from 'react';
import { type Litter } from '@/api/entities/litters';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { toast } from '@/hooks/use-toast';
import { ArrowUpDown, ArrowUp, ArrowDown, Search } from 'lucide-react';
import { InputGroup, InputGroupAddon, InputGroupInput } from '@/components/ui/input-group';
import { EditLitterDialog } from './edit-dialog';
import { AddLitterDialog } from './add-dialog';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { useLitters } from './api/useLitters';
import { useDeleteLitter } from './api/useDeleteLitter';
import { formatDateDisplay } from '@/utils/date';
import { TableEmptyState } from '@/components/ui/empty-state';

type SortConfig = {
  key: keyof Litter | 'sireName' | 'damName' | null;
  direction: 'asc' | 'desc';
};

export function LittersList() {
  const [editingLitter, setEditingLitter] = useState<Litter | null>(null);
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false);
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [deletingLitter, setDeletingLitter] = useState<Litter | null>(null);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortConfig, setSortConfig] = useState<SortConfig>({ key: null, direction: 'asc' });

  const { data, isLoading, isError, error, refetch } = useLitters({
    config: {
      staleTime: 30_000,
    },
  });

  const deleteMutation = useDeleteLitter({
    config: {
      onSuccess: () => {
        toast({
          title: 'Success',
          description: 'Litter deleted successfully',
        });
        setDeletingLitter(null);
      },
      onError: (err: Error) => {
        toast({
          title: 'Error',
          description: err.message || 'Failed to delete litter',
          variant: 'destructive',
        });
      },
    },
  });

  const handleSort = (key: keyof Litter | 'sireName' | 'damName') => {
    let direction: 'asc' | 'desc' = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const filteredAndSortedLitters = useMemo(() => {
    if (!data) return [];
    let litters = [...data] as Litter[];

    // Apply search filter
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      litters = litters.filter(
        (litter) =>
          litter.name?.toLowerCase().includes(query) ||
          litter.litterType?.toLowerCase().includes(query) ||
          litter.sire?.name?.toLowerCase().includes(query) ||
          litter.dam?.name?.toLowerCase().includes(query),
      );
    }

    // Apply sorting
    if (sortConfig.key === null) return litters;

    return litters.sort((a, b) => {
      let aValue: unknown;
      let bValue: unknown;

      // Handle nested properties
      if (sortConfig.key === 'sireName') {
        aValue = a.sire?.name || '';
        bValue = b.sire?.name || '';
      } else if (sortConfig.key === 'damName') {
        aValue = a.dam?.name || '';
        bValue = b.dam?.name || '';
      } else {
        aValue = a[sortConfig.key as keyof Litter];
        bValue = b[sortConfig.key as keyof Litter];
      }

      // Handle null/undefined values
      if (aValue == null && bValue == null) return 0;
      if (aValue == null) return 1;
      if (bValue == null) return -1;

      // Handle date sorting
      const dateFields = [
        'expectedDueDate',
        'expectedPuppyDate',
        'dobOfPuppies',
        'expectedMatingDate',
      ];
      if (sortConfig.key && dateFields.includes(String(sortConfig.key))) {
        const aDate = aValue ? new Date(aValue as string | number | Date).getTime() : 0;
        const bDate = bValue ? new Date(bValue as string | number | Date).getTime() : 0;
        return sortConfig.direction === 'asc' ? aDate - bDate : bDate - aDate;
      }

      // Handle number sorting
      if (typeof aValue === 'number' && typeof bValue === 'number') {
        return sortConfig.direction === 'asc' ? aValue - bValue : bValue - aValue;
      }

      // Handle string sorting
      const aStr = String(aValue).toLowerCase();
      const bStr = String(bValue).toLowerCase();

      if (aStr < bStr) return sortConfig.direction === 'asc' ? -1 : 1;
      if (aStr > bStr) return sortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
  }, [data, sortConfig, searchQuery]);

  const getSortIcon = (columnKey: keyof Litter | 'sireName' | 'damName') => {
    if (sortConfig.key !== columnKey) {
      return <ArrowUpDown className="ml-2 h-4 w-4 opacity-50" />;
    }
    return sortConfig.direction === 'asc' ? (
      <ArrowUp className="ml-2 h-4 w-4" />
    ) : (
      <ArrowDown className="ml-2 h-4 w-4" />
    );
  };

  const getLitterTypeLabel = (litter: Litter) => {
    if (litter.litterType === 'planned') return 'Planned';
    if (litter.litterType === 'upcoming') return 'Upcoming';
    if (litter.litterType === 'past') return 'Past';
    return '-';
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <Skeleton className="h-9 w-32 mb-2" />
            <Skeleton className="h-4 w-64" />
          </div>
          <Skeleton className="h-10 w-28" />
        </div>
        <div className="rounded-lg border bg-card shadow-sm p-4">
          <div className="grid grid-cols-6 gap-4 mb-4">
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
            <Skeleton className="h-5 w-full" />
          </div>
          <div className="space-y-3">
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
            <Skeleton className="h-12 w-full" />
          </div>
        </div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold tracking-tight">Litters</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Manage your litter records and information
            </p>
          </div>
        </div>
        <div className="rounded-lg border bg-card shadow-sm p-8">
          <div className="flex flex-col items-center justify-center text-center">
            <p className="text-sm font-medium text-destructive mb-2">Failed to load litters</p>
            <pre className="mt-2 text-xs text-muted-foreground mb-4 max-w-md">
              {(error as Error)?.message}
            </pre>
            <Button onClick={() => refetch()}>Retry</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Litters</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Manage your litter records and information
          </p>
        </div>
        <Button onClick={() => setIsAddDialogOpen(true)}>New Litter</Button>
      </div>
      <div className="flex items-center gap-2">
        <div className="flex-1 max-w-sm">
          <label htmlFor="litter-search" className="sr-only">
            Search litters
          </label>
          <InputGroup>
            <InputGroupInput
              id="litter-search"
              placeholder="Search litters..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              aria-label="Search litters"
            />
            <InputGroupAddon>
              <Search className="h-4 w-4" aria-hidden="true" />
            </InputGroupAddon>
          </InputGroup>
        </div>
      </div>
      <div className="rounded-lg border bg-card shadow-sm">
        <div className="pl-4">
          <Table>
            <TableHeader>
              <TableRow className="hover:bg-transparent">
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('name')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('name');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Name ${sortConfig.key === 'name' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'name'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Name
                    {getSortIcon('name')}
                  </button>
                </TableHead>
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('litterType')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('litterType');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Type ${sortConfig.key === 'litterType' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'litterType'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Type
                    {getSortIcon('litterType')}
                  </button>
                </TableHead>
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('sireName')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('sireName');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Sire ${sortConfig.key === 'sireName' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'sireName'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Sire
                    {getSortIcon('sireName')}
                  </button>
                </TableHead>
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('damName')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('damName');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Dam ${sortConfig.key === 'damName' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'damName'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Dam
                    {getSortIcon('damName')}
                  </button>
                </TableHead>
                <TableHead className="h-12 font-semibold">
                  <button
                    onClick={() => handleSort('expectedDueDate')}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        handleSort('expectedDueDate');
                      }
                    }}
                    className="flex items-center hover:text-foreground transition-colors"
                    aria-label={`Sort by Due Date ${sortConfig.key === 'expectedDueDate' ? (sortConfig.direction === 'asc' ? 'ascending' : 'descending') : ''}`}
                    aria-sort={
                      sortConfig.key === 'expectedDueDate'
                        ? sortConfig.direction === 'asc'
                          ? 'ascending'
                          : 'descending'
                        : 'none'
                    }
                  >
                    Due Date
                    {getSortIcon('expectedDueDate')}
                  </button>
                </TableHead>
                <TableHead className="h-12 w-[140px] text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredAndSortedLitters.map((litter) => (
                <TableRow key={litter.id} className="hover:bg-muted/50">
                  <TableCell className="font-medium py-4">{litter.name || '-'}</TableCell>
                  <TableCell className="py-4">
                    <span className="inline-flex items-center rounded-md bg-muted px-2 py-1 text-xs font-medium capitalize text-muted-foreground">
                      {getLitterTypeLabel(litter)}
                    </span>
                  </TableCell>
                  <TableCell className="py-4 text-muted-foreground">
                    {litter.sire?.name || '-'}
                  </TableCell>
                  <TableCell className="py-4 text-muted-foreground">
                    {litter.dam?.name || '-'}
                  </TableCell>
                  <TableCell className="py-4 text-muted-foreground">
                    {formatDateDisplay(
                      litter.expectedDueDate ||
                        litter.expectedPuppyDate ||
                        litter.dobOfPuppies ||
                        litter.expectedDueMonth,
                    )}
                  </TableCell>
                  <TableCell className="py-4">
                    <div className="flex items-center justify-end gap-2">
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => {
                          setEditingLitter(litter);
                          setIsEditDialogOpen(true);
                        }}
                        aria-label={`Edit ${litter.name || 'litter'}`}
                      >
                        Edit
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        onClick={() => {
                          setDeletingLitter(litter);
                          setIsDeleteDialogOpen(true);
                        }}
                        aria-label={`Delete ${litter.name || 'litter'}`}
                      >
                        Delete
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
              {filteredAndSortedLitters.length === 0 && (
                <TableEmptyState
                  title={searchQuery ? 'No litters found' : 'No litters yet'}
                  description={
                    searchQuery
                      ? 'Try adjusting your search query'
                      : 'Get started by creating your first litter record'
                  }
                  action={
                    !searchQuery
                      ? {
                          label: 'Add Litter',
                          onClick: () => setIsAddDialogOpen(true),
                        }
                      : undefined
                  }
                  colSpan={6}
                />
              )}
            </TableBody>
          </Table>
        </div>
      </div>
      <EditLitterDialog
        litter={editingLitter}
        open={isEditDialogOpen}
        onOpenChange={setIsEditDialogOpen}
      />
      <AddLitterDialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen} />
      <ConfirmDialog
        open={isDeleteDialogOpen}
        onOpenChange={setIsDeleteDialogOpen}
        onConfirm={() => {
          if (deletingLitter) {
            deleteMutation.mutate(deletingLitter);
          }
        }}
        title="Delete Litter"
        description={`Are you sure you want to delete "${deletingLitter?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="destructive"
      />
    </div>
  );
}
